import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { rapportiService } from '../../services/rapportiService';
import { logService, LogCategory } from '../../services/logService';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

// Tipo per la message box
type MessageBoxType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

interface MessageBoxState {
  isOpen: boolean;
  type: MessageBoxType;
  title: string;
  message: string;
  onConfirm?: () => void;
}

// Icona SVG per logout
const IconLogout = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

// Icona SVG per invio/upload
const IconSend = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

// Icona SVG per i log
const IconLog = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);

// Icona SVG per tornare indietro
const IconBack = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// Icona SVG per chiudere
const IconX = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Icona per successo
const IconCheckCircle = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

// Icona per errore
const IconAlertCircle = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

// Icona per warning
const IconAlertTriangle = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

// Icona per info
const IconInfo = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

// Icona per conferma (domanda)
const IconHelpCircle = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [rapportiPronti, setRapportiPronti] = useState(0);
  const [invioInCorso, setInvioInCorso] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  // State per la message box
  const [messageBox, setMessageBox] = useState<MessageBoxState>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // Funzione per mostrare la message box
  const showMessageBox = (type: MessageBoxType, title: string, message: string, onConfirm?: () => void) => {
    setMessageBox({ isOpen: true, type, title, message, onConfirm });
  };

  // Funzione per chiudere la message box
  const closeMessageBox = () => {
    setMessageBox(prev => ({ ...prev, isOpen: false }));
  };

  // Funzione per gestire la conferma
  const handleMessageBoxConfirm = () => {
    if (messageBox.onConfirm) {
      messageBox.onConfirm();
    }
    closeMessageBox();
  };

  // Funzione per ottenere l'icona in base al tipo
  const getMessageBoxIcon = () => {
    switch (messageBox.type) {
      case 'success': return <IconCheckCircle />;
      case 'error': return <IconAlertCircle />;
      case 'warning': return <IconAlertTriangle />;
      case 'info': return <IconInfo />;
      case 'confirm': return <IconHelpCircle />;
    }
  };

  // Aggiorna il conteggio dei rapporti pronti e degli errori log
  useEffect(() => {
    const updateCounts = () => {
      setRapportiPronti(rapportiService.getConteggioPronti());
      setErrorCount(logService.getRecentErrorCount());
    };

    updateCounts();

    // Aggiorna ogni secondo per riflettere i cambiamenti
    const interval = setInterval(updateCounts, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    await logout();
    navigate('/login');
  };

  // Funzione che esegue effettivamente l'invio
  const eseguiInvioRapporti = async () => {
    setInvioInCorso(true);
    try {
      const response = await rapportiService.inviaRapportiPronti();
      
      // Aggiorna il conteggio
      setRapportiPronti(rapportiService.getConteggioPronti());

      // Calcola quanti rapporti sono stati rimossi con successo
      const rimossiConSuccesso = response.salvati + response.duplicati;

      // Mostra risultato con message box integrata
      if (response.errori > 0) {
        showMessageBox(
          'warning',
          'Invio completato con errori',
          `Salvati: ${response.salvati}\nDuplicati: ${response.duplicati}\nErrori: ${response.errori}\n\n${rimossiConSuccesso} rapporti rimossi dal dispositivo.`
        );
      } else if (rimossiConSuccesso > 0) {
        showMessageBox(
          'success',
          'Rapporti inviati!',
          `Salvati: ${response.salvati}\nDuplicati: ${response.duplicati}\n\n${rimossiConSuccesso} rapporti rimossi dal dispositivo.`
        );
      } else {
        showMessageBox(
          'info',
          'Nessun invio',
          'Nessun rapporto da inviare.'
        );
      }
    } catch {
      showMessageBox(
        'error',
        'Errore di invio',
        'Errore durante l\'invio dei rapporti.\nControlla il registro log per i dettagli.'
      );
    } finally {
      setInvioInCorso(false);
    }
  };

  // Mostra la confirm box prima dell'invio
  const handleInviaRapporti = () => {
    if (rapportiPronti === 0 || invioInCorso) return;

    showMessageBox(
      'confirm',
      'Conferma invio',
      `Stai per inviare ${rapportiPronti} rapport${rapportiPronti === 1 ? 'o' : 'i'} al server.\n\nI rapporti inviati verranno rimossi dal dispositivo.\n\nVuoi procedere?`,
      eseguiInvioRapporti
    );
  };

  const handleNavigateToLogs = () => {
    navigate('/logs');
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const toggleProfileMenu = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const closeMenus = () => {
    setProfileMenuOpen(false);
  };

  // Genera le iniziali dall'utente
  const getInitials = (name: string | undefined): string => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Controlla se siamo nella pagina dei log o in una pagina diversa dalla home
  const isLogsPage = location.pathname === '/logs';
  const isHomePage = location.pathname === '/';

  return (
    <div className="layout">
      {/* Overlay per chiudere i menu */}
      {profileMenuOpen && (
        <div className="overlay" onClick={closeMenus} />
      )}

      {/* Header / Toolbar */}
      <header className="toolbar">
        {/* Pulsante indietro o spacer */}
        {!isHomePage ? (
          <button 
            className="toolbar-btn back-btn"
            onClick={handleGoBack}
            title="Torna alla Home"
            aria-label="Torna alla Home"
          >
            <IconBack />
          </button>
        ) : (
          <div className="toolbar-spacer" />
        )}

        <h1 className="toolbar-title">Pipeline</h1>

        <div className="toolbar-actions">
          {/* Pulsante Invia Rapporti */}
          <button 
            className={`toolbar-btn send-btn ${rapportiPronti === 0 ? 'disabled' : ''} ${invioInCorso ? 'loading' : ''}`}
            onClick={handleInviaRapporti}
            disabled={rapportiPronti === 0 || invioInCorso}
            title={rapportiPronti > 0 ? `Invia ${rapportiPronti} rapporti pronti` : 'Nessun rapporto da inviare'}
            aria-label="Invia rapporti"
          >
            <IconSend />
            {rapportiPronti > 0 && (
              <span className="badge-count">{rapportiPronti}</span>
            )}
          </button>

          {/* Pulsante Avatar/Profilo */}
          <button className="toolbar-btn avatar-btn" onClick={toggleProfileMenu} aria-label="Profilo">
            <span className="avatar">
              {getInitials(user?.nominativo)}
            </span>
            {errorCount > 0 && (
              <span className="badge-count badge-error">{errorCount}</span>
            )}
          </button>
        </div>
      </header>

      {/* Profile Dropdown */}
      <div className={`profile-menu ${profileMenuOpen ? 'open' : ''}`}>
        {user && (
          <>
            <div className="profile-info">
              <span className="profile-avatar">{getInitials(user.nominativo)}</span>
              <div className="profile-details">
                <span className="profile-name">{user.nominativo}</span>
                <span className="profile-username">{user.username}</span>
              </div>
            </div>
            <div className="profile-divider" />
            <button 
              className="profile-menu-item" 
              onClick={() => {
                navigate('/ore-confermate');
                closeMenus();
              }}
            >
              <span className="menu-item-icon"><IconCheckCircle /></span>
              Ore Confermate
            </button>
            <button 
              className={`profile-menu-item ${isLogsPage ? 'active' : ''}`}
              onClick={() => {
                navigate('/logs');
                closeMenus();
              }}
            >
              <span className="menu-item-icon"><IconLog /></span>
              Registro Log
              {errorCount > 0 && (
                <span className="badge-count badge-error-inline">{errorCount}</span>
              )}
            </button>
            <div className="profile-divider" />
            <button className="profile-logout" onClick={handleLogout}>
              <span className="logout-icon"><IconLogout /></span>
              Esci
            </button>
          </>
        )}
      </div>

      {/* Message Box */}
      {messageBox.isOpen && (
        <div className="message-box-overlay" onClick={messageBox.type !== 'confirm' ? closeMessageBox : undefined}>
          <div className={`message-box message-box-${messageBox.type}`} onClick={e => e.stopPropagation()}>
            <button className="message-box-close" onClick={closeMessageBox}>
              <IconX />
            </button>
            <div className="message-box-icon">
              {getMessageBoxIcon()}
            </div>
            <h3 className="message-box-title">{messageBox.title}</h3>
            <p className="message-box-message">{messageBox.message}</p>
            {messageBox.type === 'confirm' ? (
              <div className="message-box-buttons">
                <button className="message-box-button message-box-button-secondary" onClick={closeMessageBox}>
                  Annulla
                </button>
                <button className="message-box-button message-box-button-primary" onClick={handleMessageBoxConfirm}>
                  Conferma
                </button>
              </div>
            ) : (
              <button className="message-box-button" onClick={closeMessageBox}>
                OK
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;
